/**
 * music-bed.ts — procedural music beds (v2M8.7 + v4M1.7).
 *
 * Per AUDIO §5.1 the music layer has five beds. v2M8.7 shipped the
 * in-play ambient bed. v4M1.7 adds the remaining four — menu /
 * combat-overlay / victory / defeat — as procedural builders sharing
 * the same `MusicBedHandle` shape so the engine can manage them with
 * a single slot API.
 *
 * Each bed follows the same graph topology:
 *   - Three sine voices forming a chord with per-voice detune.
 *   - Low-pass filter with a slow LFO sweep.
 *   - Master gain with a slow LFO breath.
 *   - A 3 s fade-in scheduled on the master gain so `start()`-ing
 *     the sources immediately gives the intended ramp.
 *
 * Per-bed character is the chord, filter centre, LFO rates, and
 * peak gain — captured in `BED_PARAMS`. Total master peaks stay at
 * or below ≈0.18 (~ −15 dB on the music bus) so beds layer without
 * clipping when stacked (e.g. ambient + combat-overlay).
 *
 * Commissioned MP3 loops can replace any bed via
 * `AudioEngine.loadMusicSample` — the engine looks up the cached
 * sample for the bed-id at start time and falls back to procedural
 * when no sample is registered.
 */

/**
 * Bundle of nodes the engine needs to track to gracefully tear the
 * bed down. `nodes` are all the started sources that need stop()ing;
 * `masterGain` is the chain's terminal gain for the fade ramp.
 *
 * v4M1.7 renamed from `AmbientBedHandle` to `MusicBedHandle` to
 * reflect that the engine now manages multiple beds (menu, ambient,
 * combat-overlay, victory, defeat). The legacy export alias is kept
 * so external callers / tests don't churn.
 */
export interface MusicBedHandle {
  nodes: AudioScheduledSourceNode[];
  masterGain: GainNode;
}

/** Legacy alias — same shape as MusicBedHandle. Kept for v2M8.7 callers. */
export type AmbientBedHandle = MusicBedHandle;

/**
 * v4M1.7 — Music-bed identifiers. The engine keys its bed registry
 * by this so callers can start / stop / swap each bed independently.
 *
 *   - `ambient`         — in-play default music (v2M8.7).
 *   - `menu`            — main-menu music while the title screen is up.
 *   - `combat-overlay`  — layered on top of `ambient` during combat
 *                          activity; intensity-modulated by the state
 *                          machine in `combat-overlay.ts`.
 *   - `victory`         — sustained loop after the victory stinger.
 *   - `defeat`          — sustained loop after the defeat stinger.
 */
export type MusicBedId =
  | 'ambient'
  | 'menu'
  | 'combat-overlay'
  | 'victory'
  | 'defeat';

/**
 * Per-bed character parameters. Tuned to give each bed a distinct
 * emotional register while sharing the same graph topology so the
 * implementation stays cheap.
 */
interface BedParams {
  /** Chord voicing in Hz. Three voices per bed. */
  voiceFreqs: [number, number, number];
  /** Per-voice detune in cents. */
  voiceDetunes: [number, number, number];
  /** Low-pass filter centre frequency (Hz). */
  filterCentre: number;
  /** LFO depth modulating the filter centre (Hz). */
  filterDepth: number;
  /** LFO rate modulating the filter centre (Hz). */
  filterLfoRate: number;
  /** Master gain LFO rate (Hz). */
  breathRate: number;
  /** Master gain LFO depth. */
  breathDepth: number;
  /** Peak master gain on the bus. */
  peakGain: number;
  /** Fade-in duration in seconds. Default 3.0. */
  fadeIn: number;
}

const BED_PARAMS: Record<MusicBedId, BedParams> = {
  // v2M8.7 — in-play ambient: open fifth A2/E3/A3, slow breath, low filter.
  ambient: {
    voiceFreqs: [110, 165, 220],
    voiceDetunes: [0, 4, -3],
    filterCentre: 600,
    filterDepth: 200,
    filterLfoRate: 0.05,
    breathRate: 0.03,
    breathDepth: 0.04,
    peakGain: 0.18,
    fadeIn: 3.0,
  },
  // MFix13.audio — menu retune (was v4M1.7 bright open-octave "inviting"
  // chord; playtest feedback flagged it as awful). New character: darker,
  // vaster, more cinematic. E minor stack with weight at the bottom
  // (E2 / B2 / G3 — root, fifth, minor third an octave up), filter pulled
  // into the low-mid for a brooding tonal centre, slow filter LFO + breath
  // so the bed feels like a giant exhaling rather than something humming
  // along. Longer fade-in lets the mood settle before the player decides
  // anything. peakGain raised slightly to give the bass headroom — at
  // 0.16 the low-octave fundamentals were inaudible on small speakers.
  menu: {
    voiceFreqs: [82, 124, 196],
    voiceDetunes: [0, -7, 4],
    filterCentre: 420,
    filterDepth: 180,
    filterLfoRate: 0.025,
    breathRate: 0.018,
    breathDepth: 0.08,
    peakGain: 0.2,
    fadeIn: 4.0,
  },
  // v4M1.7 — combat-overlay: dissonant fourth + tritone (E3/A#3/D4),
  // faster filter sweep, slightly louder peak so the overlay reads as
  // tension when layered on top of ambient. Intensity-modulated by the
  // state machine; this is the "fully engaged" character.
  'combat-overlay': {
    voiceFreqs: [165, 233, 294],
    voiceDetunes: [0, 6, -5],
    filterCentre: 720,
    filterDepth: 350,
    filterLfoRate: 0.12,
    breathRate: 0.08,
    breathDepth: 0.06,
    peakGain: 0.14,
    fadeIn: 1.5,
  },
  // v4M1.7 — victory: major chord (A2/C#3/E3), slow major-key breath.
  // Lands underneath the victory stinger after a 1 s gap.
  victory: {
    voiceFreqs: [110, 139, 165],
    voiceDetunes: [0, 3, -2],
    filterCentre: 950,
    filterDepth: 150,
    filterLfoRate: 0.04,
    breathRate: 0.025,
    breathDepth: 0.05,
    peakGain: 0.17,
    fadeIn: 4.0,
  },
  // v4M1.7 — defeat: minor chord (A2/C3/E3), filter pulled low, slow
  // breath. Reads as gravity rather than action.
  defeat: {
    voiceFreqs: [110, 131, 165],
    voiceDetunes: [0, 3, -2],
    filterCentre: 480,
    filterDepth: 120,
    filterLfoRate: 0.03,
    breathRate: 0.02,
    breathDepth: 0.04,
    peakGain: 0.17,
    fadeIn: 4.0,
  },
};

/**
 * v4M1.7 — generic bed builder. Returns a handle the engine can
 * track and tear down on stop. Sources are NOT yet `start()`-ed — the
 * caller does that so the engine can pair start with handle
 * bookkeeping atomically. The fade-in is scheduled here against
 * `currentTime` at call time.
 */
export function buildMusicBed(
  ctx: AudioContext,
  target: AudioNode,
  bedId: MusicBedId,
): MusicBedHandle {
  const p = BED_PARAMS[bedId];
  const now = ctx.currentTime;

  // Master bed gain — receives all voices through the low-pass
  // filter, ramps from silence to peak over `fadeIn` seconds.
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.0001, now);
  masterGain.gain.exponentialRampToValueAtTime(p.peakGain, now + p.fadeIn);
  masterGain.connect(target);

  // Breath LFO — sub-audible modulation of the master gain.
  const breathLfo = ctx.createOscillator();
  breathLfo.type = 'sine';
  breathLfo.frequency.value = p.breathRate;
  const breathDepth = ctx.createGain();
  breathDepth.gain.value = p.breathDepth;
  breathLfo.connect(breathDepth);
  breathDepth.connect(masterGain.gain);

  // Low-pass filter — voices feed in, master takes the filter's
  // output. Cutoff modulated by an LFO around the bed's centre.
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = p.filterCentre;
  filter.Q.value = 1.5;
  filter.connect(masterGain);

  const filterLfo = ctx.createOscillator();
  filterLfo.type = 'sine';
  filterLfo.frequency.value = p.filterLfoRate;
  const filterDepth = ctx.createGain();
  filterDepth.gain.value = p.filterDepth;
  filterLfo.connect(filterDepth);
  filterDepth.connect(filter.frequency);

  // Three voice oscillators — sustained sines, per-voice detune for
  // chorusing. Voice gain divides total amplitude across voices so the
  // chord doesn't clip even at peak.
  const sources: AudioScheduledSourceNode[] = [];
  for (let i = 0; i < p.voiceFreqs.length; i++) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = p.voiceFreqs[i]!;
    osc.detune.value = p.voiceDetunes[i]!;
    const voiceGain = ctx.createGain();
    voiceGain.gain.value = 1 / p.voiceFreqs.length;
    osc.connect(voiceGain);
    voiceGain.connect(filter);
    sources.push(osc);
  }
  sources.push(breathLfo, filterLfo);

  return { nodes: sources, masterGain };
}

/**
 * v2M8.7 legacy wrapper — builds the ambient bed via the generic
 * builder. Callers that want the v2M8.7 shape continue to work.
 */
export function buildAmbientBed(ctx: AudioContext, target: AudioNode): MusicBedHandle {
  return buildMusicBed(ctx, target, 'ambient');
}

/**
 * v4M1.7 — sample-loop bed builder. When a commissioned MP3 has been
 * loaded via `loadMusicSample(bedId)`, the engine plays the buffer
 * via this path instead of the procedural one. Same handle shape so
 * the engine's stop / fade logic doesn't branch.
 *
 * The fade-in is scheduled identically to the procedural beds so
 * the player perceives no difference at start. Loop point uses the
 * buffer's intrinsic length; commissioned loops should be authored
 * to loop cleanly at their start/end.
 */
export function buildSampleMusicBed(
  ctx: AudioContext,
  target: AudioNode,
  bedId: MusicBedId,
  buffer: AudioBuffer,
): MusicBedHandle {
  const p = BED_PARAMS[bedId];
  const now = ctx.currentTime;

  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.0001, now);
  masterGain.gain.exponentialRampToValueAtTime(p.peakGain, now + p.fadeIn);
  masterGain.connect(target);

  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  source.connect(masterGain);

  return { nodes: [source], masterGain };
}

